import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { AuthSession, User } from '@/domain/model/User';
import { buildContainer, type Container } from '@/infrastructure/di/container';

const API_BASE = import.meta.env.VITE_API_BASE || '';

/**
 * Auth store Pinia — estado UI da sessão + injeção do container DI.
 * Regra de negócio fica nos use cases; aqui só estado, getters e dispatch.
 */
export const useAuthStore = defineStore('auth', () => {
  // Estado
  const session = ref<AuthSession | null>(null);
  const isInitialized = ref(false);
  const isRefreshing = ref(false);
  // Promise compartilhada do bootstrap pra evitar race condition:
  // sem isso, 2 navigations concorrentes pegariam isInitialized=true antes
  // do refresh terminar e redirecionariam pra /login com session ainda vazia.
  const bootstrapPromise = ref<Promise<void> | null>(null);

  // Container — injetado com hooks "this-bound" ao próprio store
  let container: Container | null = null;

  function getContainer(): Container {
    if (!container) {
      container = buildContainer(API_BASE, {
        getAccessToken: () => session.value?.accessToken ?? null,
        getRefreshToken: () =>
          session.value?.refreshToken ?? container!.sessionStorage.loadRefreshToken(),
        refresh: refreshAccessToken,
        setSession: (s) => {
          session.value = s;
        },
        clearSession: () => {
          session.value = null;
          // Reseta estado de inicialização pra permitir bootstrap após re-login.
          isInitialized.value = false;
          bootstrapPromise.value = null;
        },
      });
    }
    return container;
  }

  // Getters
  const isAuthenticated = computed(() => session.value !== null && session.value.accessToken !== '');
  const user = computed<User | null>(() => session.value?.user ?? null);
  const authorities = computed<ReadonlyArray<string>>(() => session.value?.user.authorities ?? []);

  function hasAuthority(code: string): boolean {
    return authorities.value.includes(code);
  }

  // Actions
  async function bootstrap(): Promise<void> {
    if (isInitialized.value) return;
    if (bootstrapPromise.value) {
      // Outra chamada já está rodando — espera ELA terminar pra ver session pronta.
      return bootstrapPromise.value;
    }
    bootstrapPromise.value = (async () => {
      const c = getContainer();
      const cached = c.sessionStorage.loadSession();
      if (cached) {
        session.value = cached;
        // Se access vazio (cold-start), tenta refresh imediato.
        if (cached.accessToken === '' && cached.refreshToken) {
          await refreshAccessToken();
        }
      }
      // Só marca initialized DEPOIS do refresh terminar — assim qualquer
      // beforeEach concorrente cai no ramo do bootstrapPromise e espera.
      isInitialized.value = true;
      // F0.3: carregar feature flags do tenant se há sessão válida.
      if (session.value && session.value.accessToken) {
        const { bootstrapFeatures } = await import('@/presentation/stores/featureStore');
        await bootstrapFeatures().catch(() => { /* fail-soft */ });
      }
    })();
    await bootstrapPromise.value;
  }

  async function refreshAccessToken(): Promise<string | null> {
    if (isRefreshing.value) return session.value?.accessToken ?? null;
    isRefreshing.value = true;
    try {
      const c = getContainer();
      const refresh =
        session.value?.refreshToken ?? c.sessionStorage.loadRefreshToken();
      if (!refresh) return null;
      const newSession = await c.authGateway.refresh(refresh);
      session.value = newSession;
      c.sessionStorage.saveSession(newSession);
      return newSession.accessToken;
    } catch {
      session.value = null;
      isInitialized.value = false;
      bootstrapPromise.value = null;
      getContainer().sessionStorage.clear();
      return null;
    } finally {
      isRefreshing.value = false;
    }
  }

  async function login(
    tenantSlug: string,
    email: string,
    password: string,
    rememberMe: boolean,
    totpCode?: string,
  ): Promise<'success' | 'requires2fa'> {
    const result = await getContainer().useCases.login.execute({
      tenantSlug,
      email,
      password,
      rememberMe,
      ...(totpCode !== undefined ? { totpCode } : {}),
    });
    if (result.kind === 'success') {
      // F0.3: feature flags carregadas após login bem-sucedido.
      const { bootstrapFeatures } = await import('@/presentation/stores/featureStore');
      await bootstrapFeatures().catch(() => { /* fail-soft */ });
    }
    return result.kind;
  }

  async function logout(): Promise<void> {
    await getContainer().useCases.logout.execute();
    const { useFeatureStore } = await import('@/presentation/stores/featureStore');
    useFeatureStore().reset();
    // Fix audit P1-D: reset catalog cache (multi-tenant safety).
    const { resetCatalogCache } = await import('@/presentation/composables/useCatalogCache');
    resetCatalogCache();
  }

  async function forgotPassword(tenantSlug: string, email: string): Promise<void> {
    await getContainer().useCases.forgot.execute({ tenantSlug, email });
  }

  async function resetPassword(token: string, newPassword: string): Promise<void> {
    await getContainer().useCases.reset.execute({ token, newPassword });
  }

  return {
    // estado
    session,
    isInitialized,
    isRefreshing,
    // getters
    isAuthenticated,
    user,
    authorities,
    hasAuthority,
    // acesso ao container DI (use cases não-auth — alunos, etc)
    getContainer,
    // actions
    bootstrap,
    login,
    logout,
    forgotPassword,
    resetPassword,
  };
});
